import React, { useCallback, useMemo, useState } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Link,
  TableSortLabel,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useItemAttributes, useItemRendering } from 'hooks';
import { IItem, IItemKey } from 'api/catalog/models';
import NoInformationAvailable from 'ui/ErrorDisplay/NoInformationAvailable';
import { ITEM_AVAILABILITY_COLUMNS } from 'constants/table';
import { ComputedRoute } from 'utils/ComputedRoute';

interface IProps {
  item: IItem;
}

const AvailabilityRow = ({ item }: IProps) => {
  const { getCommonAttributeRender } = useItemRendering();
  const renderCell = useCallback(
    (key: IItemKey): React.ReactNode => {
      switch (key) {
        case 'itemNumber':
          return (
            <Link
              component={RouterLink}
              to={ComputedRoute.getProductDetailsRoute(item.elasticId)}
            >
              {item.itemNumber}
            </Link>
          );
        default:
          return getCommonAttributeRender(item, key);
      }
    },
    [getCommonAttributeRender, item]
  );

  return (
    <TableRow hover>
      {ITEM_AVAILABILITY_COLUMNS.map((key) => (
        <TableCell key={key}>{renderCell(key)}</TableCell>
      ))}
    </TableRow>
  );
};

const ItemAvailability = ({ item }: IProps) => {
  const [sort, setSort] = useState<keyof IItem>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { getLabel } = useItemAttributes();
  const theme = useTheme();

  const sortedItems = useMemo(() => {
    const items: IItem[] = [...(item.itemAtOtherDcs || [])];

    switch (sort) {
      case 'itemNumber':
      case 'hostSystem':
      case 'dcNumber':
      case 'dcName':
      case 'statusCode':
      case 'isPrivateLabel':
        return items.sort((item1, item2) => {
          const found1 = item1[sort]?.toString() || '';
          const found2 = item2[sort]?.toString() || '';
          return sortDir === 'asc'
            ? found1.localeCompare(found2)
            : found2.localeCompare(found1);
        });
      default:
        return items;
    }
  }, [item.itemAtOtherDcs, sort, sortDir]);

  const handleSort = useCallback(
    (key: keyof IItem) => {
      setSort(key);
      let sortDirection: 'asc' | 'desc' = 'asc';
      if (key === sort) {
        sortDirection = sortDir === 'asc' ? 'desc' : 'asc';
      }
      setSortDir(sortDirection);
    },
    [sort, sortDir]
  );

  return sortedItems && sortedItems.length > 0 ? (
    <TableContainer>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {ITEM_AVAILABILITY_COLUMNS.map((key) => (
              <TableCell
                sortDirection={key === sort ? sortDir : false}
                key={key}
                sx={{
                  backgroundColor: theme.palette.grey[200],
                  fontWeight: 'bold',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <TableSortLabel
                  active={sort === key}
                  direction={sortDir}
                  onClick={() => handleSort(key)}
                >
                  {getLabel(key)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedItems.map((siblingItem) => (
            <AvailabilityRow key={siblingItem.elasticId} item={siblingItem} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <NoInformationAvailable minHeight='300px' />
  );
};

export default ItemAvailability;
